import { AISettings } from '../components/Settings';
import { GeminiApiClient, GeminiRewriteRequest } from './geminiApi';

export type RewriteGenre = 'general' | 'blog' | 'twitter' | 'meeting' | 'idea' | 'summary';

export interface CustomPrompt {
  id: string;
  name: string;
  genre: RewriteGenre | 'custom';
  prompt: string;
  isDefault?: boolean;
}

export interface WritingStyle {
  id: string;
  name: string;
  instruction: string;
  isDefault?: boolean;
}

export interface RewriteRequest {
  text: string;
  comment?: string;
  genre?: RewriteGenre;
  customPrompt?: string;
  writingStyle?: string;
}

export interface RewriteResponse {
  rewrittenText: string;
  tokenUsage?: {
    input: number;
    output: number;
    total: number;
  };
  error?: string;
}

export class MultiAIApiClient {
  private settings: AISettings;

  constructor(settings: AISettings) {
    this.settings = settings;
  }

  updateSettings(settings: AISettings) {
    this.settings = settings;
  }

  async rewrite(request: RewriteRequest): Promise<RewriteResponse> {
    const { text, comment, genre = 'general', customPrompt, writingStyle } = request;
    
    if (!text.trim()) {
      return {
        rewrittenText: '',
        error: '入力テキストが空です'
      };
    }

    try {
      switch (this.settings.provider) {
        case 'gemini-free':
          return await this.geminiRewrite(request, 'AIzaSyCwYScZQ_Q8nrcWkKJ-_9kKIbfQu5YP6RU');
        case 'gemini':
          if (!this.settings.apiKeys.gemini) {
            throw new Error('Gemini APIキーが設定されていません');
          }
          return await this.geminiRewrite(request, this.settings.apiKeys.gemini);
        case 'claude':
          if (!this.settings.apiKeys.claude) {
            throw new Error('Claude APIキーが設定されていません');
          }
          return await this.claudeRewrite(request);
        case 'gpt':
          if (!this.settings.apiKeys.gpt) {
            throw new Error('GPT APIキーが設定されていません');
          }
          return await this.gptRewrite(request);
        default:
          throw new Error(`未対応のプロバイダー: ${this.settings.provider}`);
      }
    } catch (error) {
      console.error('AI API Error:', error);
      return this.getMockResponse(text, genre, comment, customPrompt, writingStyle);
    }
  }

  private async geminiRewrite(request: RewriteRequest, apiKey: string): Promise<RewriteResponse> {
    // 高機能なGeminiApiClientを使用（継続リクエスト、詳細ログ、改善されたプロンプト対応）
    const geminiClient = new GeminiApiClient(apiKey);
    
    // リクエスト形式を変換
    const geminiRequest: GeminiRewriteRequest = {
      text: request.text,
      comment: request.comment,
      genre: request.genre || 'general',
      customPrompt: request.customPrompt,
      writingStyle: request.writingStyle
    };
    
    console.log('Using advanced GeminiApiClient with continuation support');
    
    // GeminiApiClientのrewriteメソッドを使用（継続リクエスト機能付き）
    return await geminiClient.rewrite(geminiRequest);
  }

  private async claudeRewrite(request: RewriteRequest): Promise<RewriteResponse> {
    const { text, comment, genre = 'general', customPrompt, writingStyle } = request;
    const prompt = customPrompt || this.buildPrompt(text, genre, comment, writingStyle);
    
    const response = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': this.settings.apiKeys.claude!,
        'anthropic-version': '2023-06-01'
      },
      body: JSON.stringify({
        model: 'claude-3-haiku-20240307',
        max_tokens: 1024,
        messages: [{
          role: 'user',
          content: prompt
        }]
      })
    });

    if (!response.ok) {
      throw new Error(`Claude API Error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    const rewrittenText = data.content[0].text;
    
    return {
      rewrittenText,
      tokenUsage: {
        input: data.usage?.input_tokens || text.length,
        output: data.usage?.output_tokens || rewrittenText.length,
        total: (data.usage?.input_tokens || text.length) + (data.usage?.output_tokens || rewrittenText.length)
      }
    };
  }

  private async gptRewrite(request: RewriteRequest): Promise<RewriteResponse> {
    const { text, comment, genre = 'general', customPrompt, writingStyle } = request;
    const prompt = customPrompt || this.buildPrompt(text, genre, comment, writingStyle);
    
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.settings.apiKeys.gpt}`
      },
      body: JSON.stringify({
        model: 'gpt-3.5-turbo',
        messages: [{
          role: 'user',
          content: prompt
        }],
        max_tokens: 1024,
        temperature: 0.7
      })
    });

    if (!response.ok) {
      throw new Error(`GPT API Error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    const rewrittenText = data.choices[0].message.content;
    
    return {
      rewrittenText,
      tokenUsage: {
        input: data.usage?.prompt_tokens || text.length,
        output: data.usage?.completion_tokens || rewrittenText.length,
        total: data.usage?.total_tokens || (text.length + rewrittenText.length)
      }
    };
  }

  private buildPrompt(text: string, genre: RewriteGenre, comment?: string, writingStyle?: string): string {
    const genrePrompts: Record<RewriteGenre, string> = {
      general: `以下の入力内容を読みやすく整形し、適切な見出し、箇条書き、強調などを使ってマークダウン形式で整理してください。

前置きや挨拶は一切不要です。整理された内容のみを出力してください。`,
      
      blog: `以下の入力内容を魅力的なブログ記事として構造化してください。
SEOを意識した見出し、読みやすい段落、適切な強調表現を使い、読者を引きつける内容にしてください。

前置きや挨拶は一切不要です。ブログ記事の本文のみを出力してください。`,
      
      twitter: `以下の入力内容を140文字以内のX（Twitter）投稿に変換してください。

重要：
- 「投稿案を作成します」などの前置きは一切不要
- 投稿本文のみを出力
- 140文字以内厳守
- 適切なハッシュタグを含める`,
      
      meeting: `以下の入力内容を議事録形式にまとめてください。

前置きや挨拶は一切不要です。議事録の内容のみを出力してください。
- 日時・場所・参加者
- 議題と討議内容  
- 決定事項
- アクションアイテム`,
      
      idea: `以下の入力内容をアイデア企画書として構造化してください。

前置きや挨拶は一切不要です。企画書の内容のみを出力してください。
- アイデアの核心
- 背景・課題
- 具体的な内容
- 実現可能性
- 次のステップ`,
      
      summary: `以下の入力内容を簡潔に要約してください。

前置きや挨拶は一切不要です。要約内容のみを出力してください。
- 主要ポイント
- 重要な数値や事実
- 結論`
    };

    let prompt = genrePrompts[genre];
    
    if (writingStyle) {
      prompt += `\n\n【ライティングスタイル指示】\n${writingStyle}`;
    }
    
    prompt += `\n\n入力内容:\n${text}`;

    if (comment) {
      prompt += `\n\n追加の指示:\n${comment}`;
    }

    return prompt;
  }

  private getMockResponse(text: string, genre: RewriteGenre, comment?: string, customPrompt?: string, writingStyle?: string): RewriteResponse {
    // カスタムプロンプト、ライティングスタイル、またはジャンルを使用
    void genre;
    void customPrompt;
    void writingStyle;
    let rewrittenText = '';
    
    if (text.includes('プロジェクト') || text.includes('計画')) {
      rewrittenText = `# プロジェクト計画書

## 概要
このプロジェクトは、ユーザーの思考をリアルタイムで整理・構造化するAIツールの開発を目的としています。

## 目標
1. 思考の整理をサポートする直感的なインターフェースの構築
2. リアルタイムでのAIによる文章リライト機能の実装
3. マークダウン形式での出力と各種サービスへの連携

## スケジュール
- フェーズ1: 要件定義と設計（2週間）
- フェーズ2: プロトタイプ開発（3週間）
- フェーズ3: テストと改善（2週間）
- フェーズ4: 本番リリース（1週間）`;
    } else {
      // デフォルトの整形
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length > 0) {
        rewrittenText = `# ${lines[0]}\n\n`;
        
        if (lines.length > 1) {
          rewrittenText += `## 概要\n${lines.slice(1).join('\n')}\n\n`;
          
          if (comment) {
            if (comment.includes('簡潔') || comment.includes('要約')) {
              rewrittenText += `## 要点\n- ${lines.slice(1).join('\n- ')}\n`;
            } else if (comment.includes('箇条書き')) {
              rewrittenText += `## 詳細\n- ${lines.slice(1).join('\n- ')}\n`;
            }
          }
        }
      }
    }
    
    return {
      rewrittenText,
      tokenUsage: {
        input: text.length,
        output: rewrittenText.length,
        total: text.length + rewrittenText.length
      }
    };
  }

  async getUsage(): Promise<{ used: number; limit: number; remaining: number }> {
    return {
      used: 1250,
      limit: 10000,
      remaining: 8750
    };
  }
}